import { useEffect, useState, useRef } from "react";
import { EXT_GET_MONTHLY_EXTENSION_IEEFTI_REPORT, EXT_GET_MONTHLY_EXTENSION_IEEFTI_REPORT_FILTERS, COUNTRY_CODE_MAP } from "../../../../constants/constant";
import { getDataWAuth } from "../../../../utils/api-service";
import { getUserCountry } from "../../../../utils/utils";
import { useSelector } from "react-redux";

export const useMonthlyExtensionIEEFTIReport = () => {
  const [rawData, setRawData] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [viewMode, setViewMode] = useState('chart'); // 'table' or 'chart' - default to chart
  const [isLoading, setIsLoading] = useState(true); // Start with loading true for initial data fetch
  const [hasNoData, setHasNoData] = useState(false);
  const hasInitialized = useRef(false);
  const userData = useSelector((state) => state.userState.userData);

  // Helper function to sort countries by their display names
  const sortCountriesByName = (countries) => {
    return countries.sort((a, b) => {
      const nameA = COUNTRY_CODE_MAP[a] || a;
      const nameB = COUNTRY_CODE_MAP[b] || b;
      return nameA.localeCompare(nameB);
    });
  };
  
  // Filter options from API
  const [newReasonCodeOptions, setNewReasonCodeOptions] = useState([]);
  const [customerNameOptions, setCustomerNameOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [currency, setCurrency] = useState('');
  
  // Separate state for applied filters (only updated when Apply button is clicked)
  const [appliedFilters, setAppliedFilters] = useState(null);
  
  // State to track initial filters after preselection (for comparison to enable/disable Apply button)
  const [initialFilters, setInitialFilters] = useState(null);
  
  // Track if user has ever manually changed any filter (to allow re-applying initial values after changes)
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  
  // Filter state (customerName moved to separate state)
  const [filters, setFilters] = useState({
    newReasonCode: '',
    volume: 'count',
    startMonth: null,
    endMonth: null,
    startYear: null,
    endYear: null,
    country: ''
  });

  // Separate state for customer name selection (not part of filters)
  const [selectedCustomerName, setSelectedCustomerName] = useState('');

  // Static options for volume
  const volumeOptions = [
    { value: 'count', label: 'Count' },
    { value: 'value', label: 'Value' }
  ];

  // Month options
  const monthOptions = [
    { value: 1, label: 'Jan' },
    { value: 2, label: 'Feb' },
    { value: 3, label: 'Mar' },
    { value: 4, label: 'Apr' },
    { value: 5, label: 'May' },
    { value: 6, label: 'Jun' },
    { value: 7, label: 'Jul' },
    { value: 8, label: 'Aug' },
    { value: 9, label: 'Sep' },
    { value: 10, label: 'Oct' },
    { value: 11, label: 'Nov' },
    { value: 12, label: 'Dec' }
  ];

  // Year options (current year and years back to 2023)
  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let year = currentYear; year >= 2023; year--) {
    yearOptions.push(year);
  }

  const handleFilterChange = (filterName, value) => {
    // Mark that user has manually interacted with filters
    setHasUserInteracted(true);
    
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };



  // Check if all required filters are filled
  const areAllFiltersValid = () => {
    return (
      // New Reason Code must be selected
      filters.newReasonCode &&
      
      // Volume must be selected (should always be selected, but check anyway)
      filters.volume &&
      
      // Date range must be complete
      filters.startMonth && 
      filters.endMonth && 
      filters.startYear && 
      filters.endYear &&
      
      // Country must be selected
      filters.country
    );
  };

  // Function to check if current filters are different from initial filters
  const haveFiltersChanged = () => {
    // If user has never interacted manually, only allow Apply if filters differ from initial
    if (!hasUserInteracted) {
      if (!initialFilters) {
        return false;
      }
      
      // Compare each filter field against initial
      const newReasonCodeChanged = filters.newReasonCode !== initialFilters.newReasonCode;
      const volumeChanged = filters.volume !== initialFilters.volume;
      const startMonthChanged = filters.startMonth !== initialFilters.startMonth;
      const endMonthChanged = filters.endMonth !== initialFilters.endMonth;
      const startYearChanged = filters.startYear !== initialFilters.startYear;
      const endYearChanged = filters.endYear !== initialFilters.endYear;
      const countryChanged = filters.country !== initialFilters.country;
      
      const hasChanged = newReasonCodeChanged || volumeChanged || startMonthChanged || 
        endMonthChanged || startYearChanged || endYearChanged || countryChanged;
      
      return hasChanged;
    }
    
    // If user has interacted, compare against applied filters (or allow if no applied filters yet)
    if (!appliedFilters) {
      return true;
    }
    
    // Compare each filter field against applied
    const newReasonCodeChanged = filters.newReasonCode !== appliedFilters.newReasonCode;
    const volumeChanged = filters.volume !== appliedFilters.volume;
    const startMonthChanged = filters.startMonth !== appliedFilters.startMonth;
    const endMonthChanged = filters.endMonth !== appliedFilters.endMonth;
    const startYearChanged = filters.startYear !== appliedFilters.startYear;
    const endYearChanged = filters.endYear !== appliedFilters.endYear;
    const countryChanged = filters.country !== appliedFilters.country;
    
    const hasChanged = newReasonCodeChanged || volumeChanged || startMonthChanged || 
      endMonthChanged || startYearChanged || endYearChanged || countryChanged;
    
    return hasChanged;
  };

  // Function to check if Apply button should be enabled
  const isApplyButtonEnabled = () => {
    const allValid = areAllFiltersValid();
    const hasChanged = haveFiltersChanged();
    
    return allValid && hasChanged;
  };

  const applyFilters = () => {
    // Set loading state first
    setIsLoading(true);
    
    // Build filter parameters for backend request
    const filterParams = {
      newReasonCode: filters.newReasonCode ? filters.newReasonCode : undefined,
      country: filters.country ? filters.country : undefined,
      startMonth: filters.startMonth,
      endMonth: filters.endMonth,
      startYear: filters.startYear,
      endYear: filters.endYear,
      volume: filters.volume
    };
    
    // Remove undefined values from the parameters
    const cleanedParams = Object.fromEntries(
      Object.entries(filterParams).filter(([_, value]) => value !== undefined)
    );
    
    // Save current filters as applied filters BEFORE making API call
    setAppliedFilters({ ...filters });
    
    // Fetch filtered data from backend
    fetchMonthlyExtensionIEEFTIReport(cleanedParams, false);
    
    return true; // Return success indicator for closing filter panel
  };

  const clearFilters = () => {
    // Reset filters to initial state
    setFilters({
      newReasonCode: '',
      volume: 'count',
      startMonth: null,
      endMonth: null,
      startYear: null,
      endYear: null,
      country: ''
    });
    // Also reset applied filters to null (this will show initial data without making new API call)
    setAppliedFilters(null);
    // Reset initial filters to null so Apply button will be enabled once user makes changes
    setInitialFilters(null);
    // Note: Don't fetch data here, let the initial data remain displayed
    // The user can apply filters again if they want to refresh
  };

  const fetchMonthlyExtensionIEEFTIReport = async (filterParams = null, isInitialLoad = false) => {
    try {
      
      // Set loading for both initial load and filter applications
      setIsLoading(true);
      
      let apiUrl = EXT_GET_MONTHLY_EXTENSION_IEEFTI_REPORT;
      
      // If filter parameters are provided, use the filtered endpoint
      if (filterParams && Object.keys(filterParams).length > 0) {
        const queryParams = new URLSearchParams();
        
        // Add filter parameters in specific sequence: newReasonCode, country, startMonth, endMonth, startYear, endYear
        if (filterParams.newReasonCode) {
          queryParams.append('newReasonCode', filterParams.newReasonCode);
        }
        if (filterParams.country) {
          queryParams.append('country', filterParams.country);
        }
        if (filterParams.startMonth) {
          queryParams.append('startMonth', filterParams.startMonth);
        }
        if (filterParams.endMonth) {
          queryParams.append('endMonth', filterParams.endMonth);
        }
        if (filterParams.startYear) {
          queryParams.append('startYear', filterParams.startYear);
        }
        if (filterParams.endYear) {
          queryParams.append('endYear', filterParams.endYear);
        }
    
        // Build the filtered API URL with query parameters
        apiUrl = queryParams.toString() 
          ? `${EXT_GET_MONTHLY_EXTENSION_IEEFTI_REPORT_FILTERS}?${queryParams.toString()}`
          : EXT_GET_MONTHLY_EXTENSION_IEEFTI_REPORT_FILTERS;
      }
      
      const response = await getDataWAuth(apiUrl);
      if (response.status === 200) {
        const data = response.data;
        
        // Check if extensionRequestList exists and has data
        if (data.extensionRequestList && data.extensionRequestList.length > 0) {
          setRawData(data.extensionRequestList);
          setHasNoData(false);
          
          // Extract currency from the first record that has currency information
          const firstRecord = data.extensionRequestList[0];
          // Use currencyKey field from the API response
          const currencyValue = firstRecord.currencyKey || 
                               firstRecord.currency || 
                               firstRecord.currencyCode || 
                               'USD'; // fallback to USD if no currency found
          setCurrency(currencyValue);
        } else {
          // No data available
          setRawData([]);
          setHasNoData(true);
          setCurrency('USD'); // Set default currency when no data
          // Clear customer name options when no data is found
          setCustomerNameOptions([]);
          // Reset selected customer name to empty
          setSelectedCustomerName('');
          
          // Even with no data, provide standard reason code categories if not already provided by newReasonCodeList
          if (!data.newReasonCodeList) {
            const standardCategories = ['F/FS', 'CF/CL', 'FU'];
            setNewReasonCodeOptions(standardCategories);
          }
        }
        
        // Process filter options even when there's no data, as long as field lists are provided
        if (data) {
          // Extract unique new reason codes from the data - categorize them as F/FS, CF/CL, FU
          if (data.newReasonCodeList || (data.extensionRequestList && data.extensionRequestList.length > 0)) {
            let newReasonCodes = data.newReasonCodeList || 
              [...new Set(data.extensionRequestList?.map(item => item.newReasonCode).filter(Boolean))];
            
            // Categorize reason codes into F/FS, CF/CL, FU
            const categorizedCodes = new Set();
            
            // Always add all possible categories to ensure they appear in dropdown even if no data
            if (data.newReasonCodeList) {
              // If backend provides the full list, categorize all codes
              newReasonCodes.forEach(code => {
                if (code.startsWith('F') && !code.startsWith('FU')) {
                  categorizedCodes.add('F/FS');
                } else if (code.startsWith('CF') || code.startsWith('CL')) {
                  categorizedCodes.add('CF/CL');
                } else if (code.startsWith('FU')) {
                  categorizedCodes.add('FU');
                }
              });
            } else {
              // If no backend list, categorize from data and add all standard categories
              newReasonCodes.forEach(code => {
                if (code.startsWith('F') && !code.startsWith('FU')) {
                  categorizedCodes.add('F/FS');
                } else if (code.startsWith('CF') || code.startsWith('CL')) {
                  categorizedCodes.add('CF/CL');
                } else if (code.startsWith('FU')) {
                  categorizedCodes.add('FU');
                }
              });
              
              // Always ensure all standard categories are available in dropdown
              categorizedCodes.add('F/FS');
              categorizedCodes.add('CF/CL');
              categorizedCodes.add('FU');
            }
            
            // Convert to array and sort with custom order: F/FS → CF/CL → FU
            const sortNewReasonCodes = (types) => {
              const priorityOrder = ['F/FS', 'CF/CL', 'FU'];
              
              return types.sort((a, b) => {
                const indexA = priorityOrder.indexOf(a);
                const indexB = priorityOrder.indexOf(b);
                
                // If both are in priority list, sort by priority order
                if (indexA !== -1 && indexB !== -1) {
                  return indexA - indexB;
                }
                // If only a is in priority list, a comes first
                if (indexA !== -1 && indexB === -1) {
                  return -1;
                }
                // If only b is in priority list, b comes first
                if (indexA === -1 && indexB !== -1) {
                  return 1;
                }
                // If neither is in priority list, sort alphabetically
                return a.localeCompare(b);
              });
            };
            
            const sortedNewReasonCodes = sortNewReasonCodes([...categorizedCodes]);
            
            // When filtering (not initial load), preserve existing options if new response has fewer options
            if (!isInitialLoad && newReasonCodeOptions.length > 0 && sortedNewReasonCodes.length < newReasonCodeOptions.length) {
              // Merge with existing options to ensure all categories remain available
              const allCategories = new Set([...newReasonCodeOptions, ...sortedNewReasonCodes]);
              setNewReasonCodeOptions(sortNewReasonCodes([...allCategories]));
            } else {
              setNewReasonCodeOptions(sortedNewReasonCodes);
            }
            
            // Preselect new reason code based on actual data for both initial load AND filter applications
            if (data.extensionRequestList && data.extensionRequestList.length > 0) {
              const firstReasonCode = data.extensionRequestList.find(item => item.newReasonCode)?.newReasonCode;
              
              let categorizedFirstCode = '';
              if (firstReasonCode) {
                if (firstReasonCode.startsWith('F') && !firstReasonCode.startsWith('FU')) {
                  categorizedFirstCode = 'F/FS';
                } else if (firstReasonCode.startsWith('CF') || firstReasonCode.startsWith('CL')) {
                  categorizedFirstCode = 'CF/CL';
                } else if (firstReasonCode.startsWith('FU')) {
                  categorizedFirstCode = 'FU';
                }
              }
              
              if (categorizedFirstCode && sortedNewReasonCodes.includes(categorizedFirstCode)) {
                setFilters(prev => ({
                  ...prev,
                  newReasonCode: categorizedFirstCode,
                  customerName: prev.customerName || '' // Preserve customer name
                }));
              } else if (isInitialLoad && sortedNewReasonCodes.length > 0) {
                // Fallback: default to first available option if no data match
                setFilters(prev => ({
                  ...prev,
                  newReasonCode: sortedNewReasonCodes[0],
                  customerName: prev.customerName || '' // Preserve customer name
                }));
              }
            } else if (isInitialLoad && sortedNewReasonCodes.length > 0) {
              // Fallback for initial load when no data available
              setFilters(prev => ({
                ...prev,
                newReasonCode: sortedNewReasonCodes[0],
                customerName: prev.customerName || '' // Preserve customer name
              }));
            }
          }
          
          // Extract customer names from API customerNameList
          if (data.customerNameList && Array.isArray(data.customerNameList) && data.customerNameList.length > 0) {
            // Use customerNameList from API response and sort alphabetically
            const customerNames = [...data.customerNameList]
              .filter(Boolean)
              .sort(); // Sort alphabetically
            
            setCustomerNameOptions(customerNames);
            
            // Reset customer name selection if current selection is not available in new list
            if (selectedCustomerName && !customerNames.includes(selectedCustomerName)) {
              setSelectedCustomerName(''); // Reset to "All Customers"
            }
          } else {
            // Fallback: extract from data if customerNameList is not available
            if (data.extensionRequestList && data.extensionRequestList.length > 0) {
              const customerNames = [...new Set(data.extensionRequestList
                .map(item => item.customerName)
                .filter(Boolean))]
                .sort(); // Sort alphabetically
              
              if (customerNames.length > 0) {
                setCustomerNameOptions(customerNames);
                
                // Reset customer name selection if current selection is not available in new list
                if (selectedCustomerName && !customerNames.includes(selectedCustomerName)) {
                  setSelectedCustomerName(''); // Reset to "All Customers"
                }
              } else {
                // No customer names found in the data
                setCustomerNameOptions([]);
                setSelectedCustomerName('');
              }
            } else {
              // No extension request list data
              setCustomerNameOptions([]);
              setSelectedCustomerName('');
            }
          }
          
          // Extract unique countries from the data
          if (data.country && Array.isArray(data.country) && data.country.length > 0) {
            // Use data.country for dropdown options (all available countries)
            const countries = data.country.filter(Boolean);
            const sortedCountries = sortCountriesByName(countries);
            setCountryOptions(sortedCountries);
            
            // Preselect country based on actual data ONLY for initial load (not filter applications)
            if (isInitialLoad && data.extensionRequestList && data.extensionRequestList.length > 0) {
              const firstCountry = data.extensionRequestList.find(item => item.country)?.country;
              
              // Preselect the country that exists in actual data and is available in country options
              if (firstCountry && sortedCountries.includes(firstCountry)) {
                setFilters(prev => ({
                  ...prev,
                  country: firstCountry,
                  customerName: prev.customerName || '' // Preserve customer name
                }));
              }
            } else if (isInitialLoad) {
              // If no data in extensionRequestList on initial load, assign country based on user's country
              const userCountry = getUserCountry(userData);
              if (userCountry && sortedCountries.includes(userCountry)) {
                setFilters(prev => ({
                  ...prev,
                  country: userCountry,
                  customerName: prev.customerName || '' // Preserve customer name
                }));
              } else if (userData?.country && sortedCountries.includes(userData.country)) {
                // Fallback: use userData.country directly if getUserCountry doesn't work
                setFilters(prev => ({
                  ...prev,
                  country: userData.country,
                  customerName: prev.customerName || '' // Preserve customer name
                }));
              }
            }
            // For non-initial loads (filter applications), preserve the existing country selection
          } else if (data.extensionRequestList && data.extensionRequestList.length > 0) {
            // Fallback: if data.country is not available, use countries from extensionRequestList
            const firstCountry = data.extensionRequestList.find(item => item.country)?.country;
            if (firstCountry) {
              const uniqueCountries = [...new Set(data.extensionRequestList.map(item => item.country).filter(Boolean))];
              const sortedCountries = sortCountriesByName(uniqueCountries);
              setCountryOptions(sortedCountries);
              setFilters(prev => ({
                ...prev,
                country: firstCountry
              }));
            }
          } else {
            // If no country data from API, try to use user's country if available in options
            const userCountry = getUserCountry(userData);
            if (userCountry) {
              setFilters(prev => ({
                ...prev,
                country: userCountry
              }));
            } else if (userData?.country) {
              setFilters(prev => ({
                ...prev,
                country: userData.countryx
              }));
            }
          }
          
          // Pre-select date range (last 6 months) if not already set
          if (isInitialLoad) {
            const currentDate = new Date();
            const currentYear = currentDate.getFullYear();
            const currentMonth = currentDate.getMonth() + 1; // getMonth() returns 0-11
            
            // Calculate 6 months ago
            let startMonth = currentMonth - 5;
            let startYear = currentYear;
            if (startMonth <= 0) {
              startMonth += 12;
              startYear -= 1;
            }
            
            setFilters(prev => ({
              ...prev,
              startMonth: startMonth,
              endMonth: currentMonth,
              startYear: startYear,
              endYear: currentYear,
              customerName: prev.customerName || '' // Preserve customer name
            }));
            
            // Also set appliedFilters for initial load so the country displays immediately
            // Use setTimeout to ensure filters state is updated first
            setTimeout(() => {
              setAppliedFilters(currentFilters => {
                // Get the current filters state (including the country that was just set)
                const updatedFilters = {
                  ...filters,
                  startMonth: startMonth,
                  endMonth: currentMonth,
                  startYear: startYear,
                  endYear: currentYear
                };
                
                // Find the country from the current data processing
                let countryToApply = '';
                if (data.country && Array.isArray(data.country) && data.country.length > 0) {
                  if (data.extensionRequestList && data.extensionRequestList.length > 0) {
                    countryToApply = data.extensionRequestList.find(item => item.country)?.country || '';
                  } else {
                    // Use user's country if no data
                    const userCountry = getUserCountry(userData);
                    if (userCountry && data.country.includes(userCountry)) {
                      countryToApply = userCountry;
                    } else if (userData?.country && data.country.includes(userData.country)) {
                      countryToApply = userData.country;
                    }
                  }
                } else if (data.extensionRequestList && data.extensionRequestList.length > 0) {
                  countryToApply = data.extensionRequestList.find(item => item.country)?.country || '';
                } else {
                  // Fallback to user country
                  const userCountry = getUserCountry(userData);
                  countryToApply = userCountry || userData?.country || '';
                }
                
                return {
                  ...updatedFilters,
                  country: countryToApply
                };
              });
            }, 0);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching monthly extension IEEFTI report:", error);
    } finally {
      // Reset loading state
      setIsLoading(false);
    }
  };

  // Effect to initialize filters with smart defaults - runs only once when component mounts
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      
      // Call the initial data fetch without any filters to get all filter options and initial data
      fetchMonthlyExtensionIEEFTIReport(null, true);
    }
  }, []);

  // Effect to capture initial filters state after preselection (to enable Apply button comparison)
  useEffect(() => {
    // Only set initial filters once, after the filters have been preselected during initial load
    if (!initialFilters && filters.extensionType && filters.country && 
        filters.startMonth && filters.endMonth && filters.startYear && filters.endYear) {
      
      setInitialFilters({ ...filters });
    }
  }, [filters, initialFilters]);

  return {
    // State
    rawData,
    reportData,
    columns,
    viewMode,
    filters,
    appliedFilters,
    currency,
    isLoading,
    hasNoData,
    selectedCustomerName,
    
    // Options
    newReasonCodeOptions,
    customerNameOptions,
    countryOptions,
    volumeOptions,
    monthOptions,
    yearOptions,
    
    // State setters
    setViewMode,
    setReportData,
    setColumns,
    setSelectedCustomerName,
    
    // Handlers
    handleFilterChange,
    applyFilters,
    clearFilters,
    areAllFiltersValid,
    isApplyButtonEnabled
  };
};